'use client'

import {
  ArrowRight,
  Badge as Award,
  MachineLearning as Brain,
  Enterprise as Building2,
  CheckmarkFilled as CheckCircle2,
  Code as Code2,
  Partnership as Handshake,
  Partnership as HeartHandshake,
  Idea as Lightbulb,
  Locked as Lock,
  Rocket,
  SecurityServices as ShieldCheck,
  ThumbsUp,
  Growth as TrendingUp,
  Group as Users,
  Lightning as Zap,
} from '@carbon/icons-react'
import Link from 'next/link'

import { BookMeetingButton } from '@/components/book-meeting-button'
import { ChromaText } from '@/components/ui/chroma-text'
import { MeshPanel } from '@/components/fx/mesh-panel'
import { PanelDots } from '@/components/fx/panel-dots'

/* Copy carried over verbatim from the previous version of this page — only
   the layout is new: mesh-gradient panels (hero + the What-we-do cards),
   the site's eyebrow/chroma header language, local icons throughout (the old
   version hotlinked a third-party icon CDN), and no justified text. */

const howWeWork = [
  {
    icon: CheckCircle2,
    text: 'Start with your business goals, not just requirements',
  },
  {
    icon: Code2,
    text: 'Design robust solutions using modern frameworks & best practices',
  },
  {
    icon: Users,
    text: 'Direct collaboration with stakeholders & leadership teams',
  },
  {
    icon: Zap,
    text: 'End-to-end: Architecture → Deployment → Ongoing support',
  },
]

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation First',
    desc: 'Constantly create, improve, and evolve',
  },
  {
    icon: ShieldCheck,
    title: 'Reliability',
    desc: 'Build solutions clients can depend on',
  },
  {
    icon: Lock,
    title: 'Integrity',
    desc: 'Transparent communication, honest execution',
  },
  {
    icon: TrendingUp,
    title: 'Scalability',
    desc: 'Built for the future, not just the present',
  },
  {
    icon: ThumbsUp,
    title: 'Client Success',
    desc: 'We succeed only when you succeed',
  },
]

const whatWeDo = [
  {
    title: 'Enterprise Software & ERP',
    desc: 'Custom solutions for accuracy, scale, seamless operations',
  },
  {
    title: 'AI-Driven Solutions',
    desc: 'Smart content, code review, productivity tools, biometric systems',
  },
  {
    title: 'Learning & Education',
    desc: 'Advanced LMS, training portals, skill development platforms',
  },
  {
    title: 'Cloud & DevOps',
    desc: 'Optimized infrastructure for speed, security, reliability',
  },
  {
    title: 'Product Engineering',
    desc: 'Idea to launch - development, testing, documentation',
  },
]

const strengths = [
  {
    icon: Building2,
    text: 'Enterprise-grade solutions trusted by high-value clients',
  },
  {
    icon: Award,
    text: 'Full-stack expertise across AI, cloud, and product engineering',
  },
  { icon: Rocket, text: 'End-to-end delivery from planning to deployment' },
  {
    icon: Code2,
    text: 'Built with modern frameworks and scalable architectures',
  },
  { icon: Brain, text: 'AI-driven automation integrated across all solutions' },
  {
    icon: ShieldCheck,
    text: 'Industry-grade security and reliability in every solution',
  },
  {
    icon: Handshake,
    text: 'Strong stakeholder management with transparent communication',
  },
  {
    icon: HeartHandshake,
    text: 'partnerships focused on continuous value creation',
  },
]

export default function AboutUsPage() {
  return (
    <main className="relative bg-background">
      {/* ── Hero — mesh gradient panel with the animated dot matrix ── */}
      <section className="container mx-auto pt-10 sm:pt-14">
        <div className="relative overflow-hidden rounded-xl p-7 sm:p-10 lg:p-14">
          <MeshPanel seedKey="about-hero" />
          <PanelDots className="text-zinc-800 dark:text-white" />
          <div className="relative max-w-3xl text-foreground">
            <div className="mb-4 flex items-center gap-3">
              <span className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground/80">
                Who We Are
              </span>
            </div>
            <h1 className="font-heading text-3xl font-normal leading-tight tracking-tight sm:text-4xl md:text-5xl">
              About Euclideum Solutions, a Campus ERP Software Company
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground/80 sm:text-base md:text-[17px]">
              A modern technology and product engineering company delivering
              enterprise-grade software, intelligent automation, cloud
              solutions, and advanced learning systems to transform how
              organizations operate and grow.
            </p>
          </div>
        </div>
      </section>

      {/* ── Our Journey ── */}
      <section className="container mx-auto pt-12 sm:pt-24">
        <div className="grid items-center gap-7 sm:gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
              <ChromaText>Our journey</ChromaText>
            </h2>
            {/* The opening line carries the section, so it is set as a
                standfirst in foreground type; the two that follow stay muted
                but at reading size rather than caption size. */}
            <p className="mt-6 text-lg leading-relaxed text-foreground lg:text-xl lg:leading-relaxed">
              Founded to bridge the gap between cutting-edge technology and
              real-world business impact.
            </p>
            <div className="mt-5 space-y-4">
              <p className="text-base leading-relaxed text-muted-foreground lg:text-[17px]">
                Organizations don't just need software, they need strategic
                partners who deliver scalable, intelligent solutions with
                operational excellence and transparent communication.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground lg:text-[17px]">
                That's exactly what we do. Deep technical expertise meets
                client-first approach. Products that transform, not just
                function.
              </p>
            </div>
            <Link
              href="/blogs/story"
              className="group/link mt-7 inline-flex items-center text-base font-medium text-foreground"
            >
              Read the full story
              <ArrowRight className="ml-2 size-4 transition-transform group-hover/link:translate-x-1" />
            </Link>
          </div>
          <div className="overflow-hidden rounded-xl">
            <img
              src="/about-us/our_journey.webp"
              alt="Our Journey"
              className="aspect-[16/10] w-full object-cover lg:aspect-auto lg:h-auto"
            />
          </div>
        </div>
      </section>

      {/* ── How We Work ── */}
      <section className="container mx-auto pt-12 sm:pt-24">
        <div className="grid items-center gap-7 sm:gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="order-2 overflow-hidden rounded-xl lg:order-1">
            <img
              src="/about-us/office2.webp"
              alt="How We Work"
              className="aspect-[16/10] w-full object-cover lg:aspect-auto lg:h-auto"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
              <ChromaText>How we work</ChromaText>
            </h2>
            <p className="mt-6 text-lg font-medium leading-relaxed text-foreground">
              Engineering excellence + client-centric delivery
            </p>
            {/* Same treatment as the values and strengths grids — these are
                statements, not footnotes, so they read at foreground weight
                with the icon standing above rather than tucked inline. */}
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-6 sm:mt-8 sm:gap-x-8 sm:gap-y-9">
              {howWeWork.map(({ icon: Icon, text }) => (
                <div key={text}>
                  <Icon className="size-6 text-emerald-600 sm:size-8 dark:text-primary-active" />
                  <p className="mt-3 text-sm font-normal leading-snug text-foreground sm:mt-4 sm:text-base lg:text-lg">
                    {text}
                  </p>
                </div>
              ))}
            </div>
            {/* Opens the Cal booking modal. This read "Meet our executive team"
                and linked to /about-us/team, which renders only a "Coming Soon"
                card — so the one CTA in this section went nowhere. */}
            <BookMeetingButton
              variant="link"
              className="group/link mt-7 inline-flex h-auto items-center p-0 text-base font-medium text-foreground no-underline hover:no-underline"
            >
              Talk to our executive team
              <ArrowRight className="ml-2 size-4 transition-transform group-hover/link:translate-x-1" />
            </BookMeetingButton>
          </div>
        </div>
      </section>

      {/* ── Guided by Our Values ── */}
      <section className="container mx-auto pt-12 sm:pt-24">
        <div className="grid items-center gap-7 sm:gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
              <ChromaText>Guided by our values</ChromaText>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground lg:text-lg">
              Five principles shape every decision and every solution:
            </p>
            {/* Set in the strengths grid's language: the icon stands on its
                own at full size, the principle is foreground type rather than
                a small bold label, and the rows get room. The green
                border-l-2 rules are gone — the eye was reading five accent
                bars before it read any of the words. */}
            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-6 sm:mt-8 sm:gap-x-8 sm:gap-y-9">
              {values.map(({ icon: Icon, title, desc }) => (
                <div key={title}>
                  <Icon className="size-6 text-emerald-600 sm:size-8 dark:text-primary-active" />
                  <h3 className="mt-3 text-base font-normal leading-snug text-foreground sm:mt-4 sm:text-lg lg:text-xl">
                    {title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground sm:mt-2 sm:text-base">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-xl">
            <img
              src="/about-us/office3.webp"
              alt="Guided by Our Values"
              className="aspect-[16/10] w-full object-cover lg:aspect-auto lg:h-auto"
            />
          </div>
        </div>
      </section>

      {/* ── What We Do — plain surface cards ── */}
      <section className="container mx-auto pt-12 sm:pt-24">
        <div className="grid items-center gap-7 sm:gap-10 lg:grid-cols-2 lg:gap-16">
          <div className="order-2 overflow-hidden rounded-xl lg:order-1">
            <img
              src="/about-us/what_we_do.webp"
              alt="What We Do"
              className="aspect-[16/10] w-full object-cover lg:aspect-auto lg:h-auto"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
              <ChromaText>What we do</ChromaText>
            </h2>
            <div className="mt-6 space-y-3">
              {whatWeDo.map(({ title, desc }) => (
                <div
                  key={title}
                  className="rounded-xl border border-border bg-card p-4 text-foreground sm:p-5"
                >
                  <h3 className="font-heading text-lg font-semibold leading-snug lg:text-xl">
                    {title}
                  </h3>
                  <p className="mt-1.5 text-[15px] leading-relaxed text-foreground/75 lg:text-base">
                    {desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Strengths ── */}
      <section className="container mx-auto pt-12 sm:pt-24">
        <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:gap-x-8 sm:gap-y-10 lg:grid-cols-4">
          {strengths.map(({ icon: Icon, text }) => (
            <div key={text} className="space-y-3 sm:space-y-4">
              <Icon className="size-6 text-emerald-600 sm:size-8 dark:text-primary-active" />
              <h3 className="text-sm font-normal leading-snug text-foreground sm:text-lg lg:text-xl">
                {text}
              </h3>
            </div>
          ))}
        </div>
      </section>

      {/* ── Together ── */}
      <section className="container mx-auto pb-14 pt-12 sm:pb-24 sm:pt-24">
        <div className="grid items-center gap-7 sm:gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <h2 className="font-heading text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl">
              <ChromaText>Building the future together</ChromaText>
            </h2>
            <div className="mt-6 space-y-7">
              <p className="text-lg leading-relaxed text-muted-foreground lg:text-xl lg:leading-relaxed">
                Collaboration. Innovation. Precision. We're building systems
                that empower industries, uplift teams, and unlock opportunities,
                transforming how organizations use technology to reach their
                boldest goals.
              </p>
              {/* The closing line is the point of the section — set it as a
                  statement rather than a tinted chip of caption type. */}
              <p className="font-heading text-xl font-normal leading-snug text-foreground lg:text-2xl">
                We don&apos;t just build software - we craft the future.
              </p>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl">
            <img
              src="/about-us/office5.webp"
              alt="Building the future together"
              className="aspect-[16/10] w-full object-cover lg:aspect-auto lg:h-auto"
            />
          </div>
        </div>
      </section>
    </main>
  )
}
